import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';

import { ReportTableComponent } from '../../reports/shared/report-table/report-table.component';
import {
    TabularReportColumn,
    TabularReportRow,
} from '../../../app-layout/shared/tabular-report/tabular-report.config';
import { LinkButtonDirective } from '../../shared/directives/button.directive';
import { GenerateReportCardPayload } from '../../../../services/gable/gable.service';

/**
 * Inline rendering of a `generate_report` tool result inside the Gable
 * chat. Mirrors the layout of the standalone Reports pages
 * (e.g. `/app/reports/profit-vs-loss-ledger`) — same `<app-report-table>`,
 * same Export CSV / Save as PDF buttons — plus a "View in Reports"
 * deeplink that takes the user to the full standalone page if they want
 * filters, comparison mode, etc.
 *
 * Auto-hides each of the three buttons if its url/deeplink is missing:
 *  - PDF/CSV URLs are 24h-signed and always come back on a success
 *  - reports_page_url is null for packets-only reports (see
 *    `Report.route` in ReportsTableSeeder — a handful of entries have
 *    `route: null` because they're only reachable as PDF bundles).
 */
@Component({
    selector: 'app-gable-report-card',
    templateUrl: './report-card.component.html',
    styleUrls: ['./report-card.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        RouterModule,
        MatButtonModule,
        ReportTableComponent,
        LinkButtonDirective,
    ],
})
export class GableReportCardComponent {
    @Input({ required: true }) payload!: GenerateReportCardPayload;

    get columns(): TabularReportColumn[] {
        return (this.payload?.data?.columns ?? []) as TabularReportColumn[];
    }

    get rows(): TabularReportRow[] {
        return (this.payload?.data?.rows ?? []) as TabularReportRow[];
    }

    get pdfUrl(): string | null {
        return this.payload?.downloads?.pdf?.url ?? null;
    }

    get csvUrl(): string | null {
        return this.payload?.downloads?.csv?.url ?? null;
    }

    get reportsPageUrl(): string | null {
        return this.payload?.reports_page_url ?? null;
    }

    get dateRange(): string {
        return this.payload?.date_range ?? '';
    }

    get reportName(): string {
        return this.payload?.report_name ?? '';
    }

    get orgName(): string {
        return this.payload?.org_name ?? '';
    }
}
